import { createClient } from '@supabase/supabase-js';
import { workforceFetch } from './workforceBff';

// Talk to Supabase through our own origin (/sb), not *.supabase.co directly.
// Supabase's endpoints are filtered from Iran, so direct calls forced
// operators to use a VPN to register or log in even though the site itself is
// reachable. Our server proxies /sb/* to the real project (server/routes/
// supabaseProxy.js). supabase-js derives /auth/v1 and /rest/v1 from this base.
// In production this is same-origin; the CRA dev server forwards unknown paths
// to :3001 in development, so /sb works there too.
//
// The anon key is intentionally still shipped in the client: it is public by
// design and RLS/Auth - unchanged - remain the only trust boundary. Keeping it
// client-side keeps the auth handshake byte-for-byte identical to before, so
// the proxy is purely a network-path change with no behavioral risk.
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const proxyPath = process.env.REACT_APP_SUPABASE_PROXY_PATH || '/sb';
const origin = typeof window !== 'undefined' ? window.location.origin : '';
const url = origin + proxyPath;

// supabase-js derives its localStorage session key from the supabaseUrl host,
// so it is pinned explicitly. It used to be pinned to the Supabase Cloud project
// ref (sb-xbaldryekyeansuvuhym-auth-token) so the /sb proxy cutover kept sessions.
// On 2026-09-25 the database moved to our self-hosted Supabase: a session saved
// under the old key carries a token signed by the Cloud project, which the new
// API rejects. Use a project-neutral key, and drop the old copy locally only.
// Never through signOut: that would revoke a refresh token the BFF may hold.
const storageKey = 'sb-fttx-auth-token';
const LEGACY_STORAGE_KEYS = ['sb-xbaldryekyeansuvuhym-auth-token'];
if (typeof window !== 'undefined') {
  try {
    LEGACY_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
  } catch (_) {
    // Storage can be unavailable (private mode); nothing to clean up then.
  }
}

if (!anonKey) {
  // Fail loudly in development if env is missing.
  // eslint-disable-next-line no-console
  console.error('Supabase env missing: set REACT_APP_SUPABASE_ANON_KEY');
}

export const supabase = createClient(url, anonKey, {
  global: { fetch: workforceFetch },
  auth: {
    storageKey,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

// After an approved workforce login is exchanged for the host-only BFF cookie,
// remove only the browser persistence. Calling Supabase signOut here would revoke
// the refresh token now held by the BFF and could strand the administrator.
export function clearPersistedSupabaseSession() {
  if (typeof window !== 'undefined') window.localStorage.removeItem(storageKey);
  supabase.auth.stopAutoRefresh();
}
